import React from 'react';
import moment from 'moment';
import DOMPurify from 'dompurify';
import renderHTML from 'react-render-html';
import { Mentions } from 'antd';
import optimizeImage from '../../shared/utils/imageOptimizer';

const { Option: MentionOption } = Mentions;

export const alphabetizeGroups = (groups) => {
  if (!Array.isArray(groups)) {
    return [];
  }
  if (groups.length === 0) {
    return [];
  }
  return groups.sort((a, b) => a.name.localeCompare(b.name, undefined, {
    numeric: true,
    sensitivity: 'base',
  }));
};

export const buildSubmissionObject = (
  text,
  uploadMedia,
  additionalProps = {},
) => {
  const submissionOb = {
    postText: text,
    ...additionalProps,
  };

  if (Object.keys(uploadMedia).length) {
    submissionOb.mediaId = uploadMedia.id;
  }

  return submissionOb;
};

// TO-DO: SWITCH MOMENT TO DATE-FNS
export const dateCreator = (date) => moment(date || new Date()).format('MMMM Do, YYYY');

export const editMentionText = (inputText) => {
  if (inputText) {
    let text = inputText;
    text = text.replace(/&nbsp;/g, '');
    text = text.replace(/\[u=\d+\|(.*?)\]/g, '@$1');
    return text;
  }
  return '';
};

export const extractMentionsFromText = (text) => {
  if (!text) return [];

  const mentionRegex = /\[u=(\d+)\|([^\]]+)\]/g;
  const mentions = [];
  let match;

  // eslint-disable-next-line no-cond-assign
  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push(match[0]);
  }

  return mentions;
};

export const filterUserMentions = (
  userMentionsArray,
  setUserMentionsFunction,
  messageBodyString,
) => {
  const filteredMentions = userMentionsArray.filter((mention) => {
    const match = mention.match(/\[u=(.+?)\|(.+?) (.+?)\]/);
    if (!match) return false;

    const firstName = match[2];
    const lastName = match[3];
    const displayName = `@${firstName} ${lastName}`;

    return messageBodyString.includes(displayName);
  });

  setUserMentionsFunction(filteredMentions);
};

export const findCommentIndex = (comment, feedObject) => feedObject.replies.findIndex(
  (commentItem) => commentItem.id === comment.id,
);

export const findFeedIndex = (feedObject, feed, pinnedFeed) => {
  if (feedObject.pinned) {
    return pinnedFeed.findIndex((feedItem) => feedItem.id === feedObject.id);
  }
  return feed.findIndex((feedItem) => feedItem.id === feedObject.id);
};

export const findReplyIndex = (reply, comment) => comment.replies.findIndex(
  (replyItem) => replyItem.id === reply.id,
);

export const generateErrorMessage = (contentType = '', userAction = '') => {
  if (!userAction || !contentType) return 'Unknown error occurred. Please try again later.';
  const errorMessage = `An error occurred when ${userAction} the ${contentType}. Please try again later.`;
  return errorMessage;
};

export const imageFlipper = (media) => {
  if (media.type === 'VIDEO' && media.thumbnailKey) {
    return media.thumbnailUrl;
  }
  return media.mediaUrl;
};

export const pictureSelector = (url, size1 = 70, size2 = 60) => {
  const defaultImage = 'https://s3.amazonaws.com/teambuildr-staging-assets/pic/default.png';

  if (url) {
    return optimizeImage(url, {
      resize: {
        width: size1,
        height: size1,
        fit: 'contain',
      },
    });
  }
  return optimizeImage(defaultImage, {
    resize: {
      width: size2,
      height: size2,
      fit: 'contain',
    },
  });
};

// Transforms the message string to include properly formatted mentions
export const replaceMentionsInMessage = (messageString, userMentions) => {
  if (!messageString || !userMentions || userMentions.length === 0) {
    return messageString;
  }

  // First, extract all the names from userMentions
  const mentionNames = userMentions.map((mentionStr) => {
    const match = mentionStr.match(/\[u=(.+?)\|(.+?)\]/);
    return match ? match[2] : null;
  }).filter(Boolean);

  // Create a regex pattern that matches any of the mention names
  const namesPattern = mentionNames.map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const regex = new RegExp(`@(${namesPattern})(?=\\s|$)`, 'g');

  return messageString.replace(regex, (fullMatch, mentionText) => {
    // Find the corresponding mention string
    const matchedMention = userMentions.find((mentionStr) => {
      const match = mentionStr.match(/\[u=(.+?)\|(.+?)\]/);
      return match && match[2] === mentionText;
    });

    return matchedMention || fullMatch;
  });
};

export const replaceUrls = (text) => {
  const exp = /(\b((https?|ftp|file):\/\/|(www))[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|]*)/gi;
  return text.replace(exp, '<a href=\'$1\'>$1</a>');
};

export const shouldAddToFeed = (createdPostGroups, selectedGroups) => {
  if (!selectedGroups.length) return true;

  const createdGroupIds = createdPostGroups.map((group) => group.id);
  const selectedGroupIds = selectedGroups.map((group) => group.id);

  return createdGroupIds.some((groupId) => selectedGroupIds.includes(groupId));
};

export const textSlicer = (text, length = 70) => {
  if (text.length > length) {
    return `${text.slice(0, length)}...`;
  }
  return text;
};

export const transformPostText = (inputText = '', showBold = false) => {
  if (!inputText) return '';
  let text = inputText.replace(/&nbsp;/g, '');
  const replacement = showBold
    ? '<span style="font-weight: bold;">$1</span>'
    : '@$1';
  text = text.replace(/\[u=\d+\|(.*?)\]/g, replacement);
  if (showBold) {
    text = replaceUrls(text);
    const clean = DOMPurify.sanitize(text, { USE_PROFILES: { html: true } });
    return renderHTML(clean);
  }
  return text;
};

export const urlSnapper = (uploadMedia, isMediaUploaded = true) => {
  if (!uploadMedia || !isMediaUploaded) return '';
  return uploadMedia.thumbnailUrl || uploadMedia.mediaUrl || '';
};

export const userParser = (users) => users.map((user) => (
  <MentionOption
    key={user.id}
    value={`${user.first} ${user.last}`}
  >
    {`${user.first} ${user.last}`}
  </MentionOption>
));
